import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Create as BaseCreate,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
  ArrayInput,
  NumberInput,
  required,
  email,
  DateInput,
  SelectInput,
  BooleanInput,
  FormDataConsumer,
  useGetOne,
  useNotify,
  useRedirect,
  useDataProvider,
} from "react-admin";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import Map from "../../components/Map";
import countryCodes from "./phone_code.json";
import moment from "moment";
import { useNotification } from "../../NotificationContext";
import { checkPhoneExists } from "../../components/dataProvider";

const PREFIX = "Create";

const classes = {
  inlineBlock: `${PREFIX}-inlineBlock`,
};

const StyledBaseCreate = styled(BaseCreate)({
  [`& .${classes.inlineBlock}`]: { display: "inline-flex", marginRight: "1rem" },
});

export const Create = ({ ...props }) => {
  const [, setSelectedCountryCode] = useState("591");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const { addNotification } = useNotification();
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const handleCountryChange = (event) => {
    setSelectedCountryCode(event.target.value);
  };

  const handleError = (error) => {
    notify("Error al crear el cliente", { type: "error" });
  };

  const handleSubmit = async (data) => {
    // Si no hay teléfono o es "0", crear directamente
    if (!data.phone || data.phone === "0") {
      return createCustomer(data);
    }

    setIsCheckingPhone(true);
    try {
      const result = await checkPhoneExists(data.phone);

      if (result.exists) {
        // Mostrar modal de confirmación
        setPendingData(data);
        setShowConfirmModal(true);
      } else {
        // Crear cliente directamente
        createCustomer(data);
      }
    } catch (error) {
      notify("Error al verificar el teléfono", { type: "error" });
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const createCustomer = async (data) => {
    try {
      const result = await dataProvider.create("enterprise/customers", { data });
      notify("Cliente creado exitosamente", { type: "success" });
      redirect("/enterprise/customers");
      return result;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  const handleConfirmCreate = () => {
    setShowConfirmModal(false);
    createCustomer(pendingData);
    setPendingData(null);
  };

  const handleCancelCreate = () => {
    setShowConfirmModal(false);
    setPendingData(null);
  };

  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;
  const { data: provider } = useGetOne("providers", { id: providerId });
  return (
    <>
      <StyledBaseCreate
        {...props}
        onError={handleError}
        transform={(data) => {
          const today = moment().format("YYYY-MM-DD");

          if (!data.birthdate) return data;

          const stored = JSON.parse(localStorage.getItem("birthdaysShown") || "{}");

          if (stored.date !== today) {
            stored.date = today;
            stored.ids = [];
          }

          const updatedIds = [...stored.ids];

          const birthdate = moment.utc(data.birthdate);
          const todayMoment = moment();
          const tomorrow = moment().add(1, "day");

          const birthdayThisYear = moment().month(birthdate.month()).date(birthdate.date());

          const isToday = birthdayThisYear.isSame(todayMoment, "day");
          const isTomorrow = birthdayThisYear.isSame(tomorrow, "day");

          if (isToday || isTomorrow) {
            const id = `birthday-${data.fullName}-${isToday ? "today" : "tomorrow"}`;

            if (!updatedIds.includes(id)) {
              addNotification({
                id,
                title: "🎉 Cumpleaños",
                message: `${isToday ? "Hoy" : "Mañana"} cumple años ${data.fullName}`,
              });
              updatedIds.push(id);

              localStorage.setItem("birthdaysShown", JSON.stringify({ date: today, ids: updatedIds }));
            }
          }
          return data;
        }}
      >
        <SimpleForm onSubmit={handleSubmit}>
          <TextInput validate={[required()]} source="fullName" label="Nombre Completo" />
          <SelectInput
            label="Código de País"
            source="countryCode"
            choices={countryCodes.paises}
            optionText={(value) => `${value.name} ( +${value.dial_code} )`}
            optionValue="dial_code"
            onChange={handleCountryChange}
            validate={[required()]}
            defaultValue={countryCodes.paises.find((e) => e.code === "BO")?.dial_code}
          />
          <NumberInput source="phone" label="Teléfono/Celular" validate={[required()]} />
          <TextInput validate={[email()]} source="email" label="Email" />
          <DateInput
            label="Fecha de Nacimiento"
            source="birthdate"
            parse={(value) => {
              if (!value) return null;
              return moment(value, "YYYY-MM-DD").toDate(); // devuelve Date
            }}
            format={(value) => {
              if (!value) return null;
              return moment(value).format("YYYY-MM-DD"); // muestra en input
            }}
          />
          <SelectInput
            label="Género"
            source="gender"
            choices={[
              { id: "hombre", name: "Hombre" },
              { id: "mujer", name: "Mujer" },
            ]}
          />
          {provider?.reminderConfirmed && (
            <>
              <BooleanInput source="reminderEnabled" label="Activar Recordatorio" />
              <FormDataConsumer>
                {({ formData }) =>
                  formData?.reminderEnabled ? (
                    <span style={{ marginBottom: 20, color: "gray" }}>
                      {" "}
                      * La notificación del recordatorio <br /> enviará a las 08:00 am
                    </span>
                  ) : null
                }
              </FormDataConsumer>
            </>
          )}

          <ArrayInput source="addresses" label="Direcciones">
            <SimpleFormIterator>
              <Map />
              {/* <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}> */}
              <TextInput validate={[required()]} source="name" label="Nombre" />
              <TextInput validate={[required()]} source="street" label="Calle" />
              <TextInput source="house" label="Número de casa" />
              <TextInput source="reference" label="Referencia" />
              {/* </div> */}
            </SimpleFormIterator>
          </ArrayInput>
        </SimpleForm>
      </StyledBaseCreate>

      {/* Modal de confirmación para teléfono duplicado */}
      <Dialog open={showConfirmModal} onClose={handleCancelCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Teléfono ya registrado</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ya existe un cliente registrado con el número de teléfono <strong>{pendingData?.phone}</strong>.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ¿Deseas continuar y crear el cliente de todas formas?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} color="secondary" variant="outlined">
            No, cancelar
          </Button>
          <Button onClick={handleConfirmCreate} color="primary" variant="contained" disabled={isCheckingPhone}>
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
